import { ChangeDetectorRef, Component, Input, OnInit } from "@angular/core";
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  Validators,
} from "@angular/forms";
import { HttpErrorsService } from "@sharedV11/services/alerts/errors/http-errors.service";
import { SnackbarInfoService } from "@sharedV11/services/alerts/snackbar-info/snackbar-info.service";
import { ComplementosV11Service } from "@sharedV11/services/components/entradas-y-promociones/complementos-v11/complementos-v11.service";
import { GlobalServicCentros } from "@sharedV11/services/global/global.service centros";
import { PanelService } from "@sharedV11/services/panel/panel.service";
import { Translator } from "@sharedV11/services/translator/translator.service";
import { DualListComponent } from "angular-dual-listbox";
import { Subject } from "rxjs";
import { first, takeUntil } from "rxjs/operators";

@Component({
  selector: "app-edit-complemento-v11",
  templateUrl: "./edit-complemento-v11.component.html",
  styleUrls: ["./edit-complemento-v11.component.scss"],
})
export class EditComplementoV11Component implements OnInit {
  loading = true;
  private ngUnsubscribe = new Subject();

  @Input() pkId: any;
  @Input() mode: string;
  @Input() modificar: boolean;
  @Input() otherProps: { [key: string]: any };

  translation: any;
  complementoForm: FormGroup;
  centrosSinAsignar: { pkId: string; Nombre: string }[] = [];
  centrosAsignados: { pkId: string; Nombre: string }[] = [];
  complementoInicial: any;
  /** Dados do complemento carregado (para fallback de CentrosNombres quando tipoCentros vazio). */
  private complementoRow: { CentrosIds?: string; CentrosNombres?: string } | null = null;
  format: {
    add: string;
    remove: string;
    all: string;
    none: string;
    direction: string;
    draggable: boolean;
    locale: string;
  };

  /** Sequências de caracteres proibidas pelo backend para estes campos. */
  private static readonly TEXTO_PROIBIDO = [";", "@@", "--", "/*", "*/", "//", "\\\\"];

  /** Remove do texto todas as sequências proibidas. */
  static sanitizarTexto(valor: string): string {
    let s = (valor ?? "").toString();
    EditComplementoV11Component.TEXTO_PROIBIDO.forEach((seq) => {
      s = s.split(seq).join("");
    });
    return s;
  }

  constructor(
    private fb: FormBuilder,
    private translator: Translator,
    private cdRef: ChangeDetectorRef,
    private panelService: PanelService,
    private snackvar_service: SnackbarInfoService,
    private complementosService: ComplementosV11Service,
    private httpErrorService: HttpErrorsService,
    private gsCentros: GlobalServicCentros
  ) {}

  ngOnInit(): void {
    this.initializeTranslate();
    this.initializeForm();
    this.initializeFormat();
    this.loadCentrosAndManageForm();
  }

  initializeFormat() {
    this.format = {
      add: this.translation?.Anadir ?? "Añadir",
      remove: this.translation?.Borrar ?? "Borrar",
      all: this.translation?.Todos ?? "Todos",
      none: this.translation?.Ninguno ?? "Ninguno",
      direction: DualListComponent.LTR,
      draggable: true,
      locale: "es",
    };
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  initializeTranslate() {
    this.translation = this.translator.GetTranslations();
  }

  initializeForm() {
    this.complementoForm = this.fb.group({
      pkId: [""],
      Nombre: [
        "",
        [
          Validators.required,
          Validators.maxLength(100),
          this.forbiddenCharsValidator,
        ],
      ],
      Descripcion: [
        "",
        [Validators.maxLength(255), this.forbiddenCharsValidator],
      ],
      CentrosIds: [[] as string[]],
    });
  }

  /** Carrega centros via GlobalServicCentros (mesma fonte dos outros dual-lists) e depois manageForm. */
  async loadCentrosAndManageForm() {
    await this.gsCentros.getCentros();
    this.buildCentrosLists();
    this.manageForm(this.pkId, this.mode);
    this.cdRef.detectChanges();
  }

  /** Divide centros en disponibles (source) y asignados (destination) según CentrosIds.
   * IMPORTANTE: angular-dual-listbox exige que os itens em destination existam em source.
   * Por isso source = TODOS os centros; o dual-list move os asignados para a direita internamente. */
  buildCentrosLists() {
    const ids = this.complementoForm?.get("CentrosIds")?.value ?? [];
    const centrosIdsStr = Array.isArray(ids)
      ? ids.map((x: any) => String(x).trim()).filter(Boolean).join(",")
      : (typeof ids === "string" ? ids : "");

    const registro = { CentrosIds: centrosIdsStr };
    this.centrosAsignados = [...(this.gsCentros.daTiposCentros(registro) ?? [])];

    // Fallback: si tipoCentros está vacío, construir asignados desde CentrosNombres (API)
    if (this.centrosAsignados.length === 0 && centrosIdsStr && this.complementoRow?.CentrosNombres) {
      const idsArr = centrosIdsStr.split(",").map((s) => s.trim()).filter(Boolean);
      const nombresArr = this.complementoRow.CentrosNombres
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
      this.centrosAsignados = idsArr.map((id, i) => ({
        pkId: id,
        Nombre: nombresArr[i] ?? "Centro " + id,
      }));
    }

    const todos = this.gsCentros.filtrarTiposCentros() ?? [];
    // O dual-listbox precisa que os itens em destination existam em source.
    // Se centrosAsignados tem itens que não estão em todos, adicioná-los ao source.
    const idsSet = new Set(
      centrosIdsStr ? centrosIdsStr.split(",").map((s) => s.trim()).filter(Boolean) : []
    );
    const enTodos = new Set(
      (todos as any[]).map((c) => String(c?.pkId ?? c?.PkId ?? c?.pkID ?? c?.Id ?? c?.id ?? ""))
    );
    const faltamNoTodos = this.centrosAsignados.filter(
      (a: any) => !enTodos.has(String(a?.pkId ?? a?._id ?? ""))
    );
    this.centrosSinAsignar = [...todos, ...faltamNoTodos];
  }

  /** Sincroniza CentrosIds del form cuando el usuario mueve items en el dual-list. */
  onCentrosMoved() {
    const idsStr = this.gsCentros.obtenercentrosAsociadosconOrden(this.centrosAsignados);
    const ids = idsStr ? idsStr.split(",").map((s) => s.trim()).filter(Boolean) : [];
    this.complementoForm.patchValue({ CentrosIds: ids }, { emitEvent: true });
    if (this.complementoInicial != null) {
      this.panelService.setDiscardChanges(false);
    }
    this.cdRef.detectChanges();
  }

  manageForm(pkId: any, mode: string) {
    if (mode === "I") {
      this.complementoRow = null;
      this.complementoForm.patchValue({ pkId: "0" });
      this.complementoInicial = this.complementoForm.value;
      this.loading = false;
      this.valueChanged();
      this.cdRef.detectChanges();
      return;
    }
    if (pkId != null && pkId !== "") {
      this.loadComplemento(pkId);
    } else {
      this.complementoInicial = this.complementoForm.value;
      this.loading = false;
      this.valueChanged();
      this.cdRef.detectChanges();
    }
  }

  loadComplemento(pkId: string) {
    this.complementosService
      .getComplementoById(pkId)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(
        (data) => {
          const lista =
            data?.DatosResult?.Lista ?? data?.DatosResult?.lista ?? [];
          const row = lista[0];
          if (row) {
            this.complementoRow = {
              CentrosIds: row.CentrosIds ?? row.centrosIds ?? "",
              CentrosNombres: row.CentrosNombres ?? row.centrosNombres ?? "",
            };
            const centrosIdsStr = this.complementoRow.CentrosIds;
            const centrosIdsArr = centrosIdsStr
              ? centrosIdsStr.toString().split(",").map((s: string) => s.trim()).filter(Boolean)
              : [];
            this.complementoForm.patchValue({
              pkId: row.pkId ?? row.pkID ?? "",
              Nombre: row.Nombre ?? row.nombre ?? "",
              Descripcion: row.Descripcion ?? row.descripcion ?? "",
              CentrosIds: centrosIdsArr,
            });
            this.complementoInicial = this.complementoForm.value;
            this.buildCentrosLists();
          }
          this.loading = false;
          this.valueChanged();
          this.cdRef.detectChanges();
        },
        (error) => {
          this.httpErrorService.identificarErrores(error);
          this.loading = false;
          this.cdRef.detectChanges();
        }
      );
  }

  valueChanged() {
    this.complementoForm.valueChanges
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((value) => {
        if (this.complementoInicial != null) {
          this.panelService.setDiscardChanges(false);
          if (JSON.stringify(value) === JSON.stringify(this.complementoInicial)) {
            this.panelService.setDiscardChanges(true);
          }
        }
      });
  }

  get formControls() {
    return this.complementoForm.controls;
  }

  destroy() {
    this.panelService.setDiscardChanges(true);
    this.panelService.setClosePanel(null);
  }

  save() {
    this.panelService.setSavePanel(null);
  }

  discard() {
    if (this.complementoInicial != null) {
      this.complementoForm.reset(this.complementoInicial);
    } else {
      this.complementoForm.reset({
        pkId: this.mode === "D" ? "0" : this.complementoForm.get("pkId")?.value ?? "0",
        Nombre: "",
        Descripcion: "",
        CentrosIds: [],
      });
    }
    this.buildCentrosLists();
    this.complementoForm.markAsPristine();
    this.complementoForm.markAsUntouched();
    this.cdRef.detectChanges();
  }

  submit() {
    this.complementoForm.markAllAsTouched();
    if (this.complementoForm.invalid) {
      return;
    }
    const value = this.complementoForm.value;
    const nombreSan = EditComplementoV11Component.sanitizarTexto(
      value.Nombre ?? ""
    );
    const descripcionSan = EditComplementoV11Component.sanitizarTexto(
      value.Descripcion ?? ""
    );

    const centrosIdsStr = this.gsCentros.obtenercentrosAsociadosconOrden(
      this.centrosAsignados
    );
    const CentrosIds =
      centrosIdsStr && centrosIdsStr.trim() ? centrosIdsStr : undefined;
    const pkId =
      this.mode === "D" ? "0" : value.pkId != null ? String(value.pkId) : "0";
    const payload = {
      pkId,
      Nombre: nombreSan,
      Descripcion: descripcionSan || undefined,
      CentrosIds,
    };
    this.loading = true;
    this.complementosService
      .insUpdComplemento(payload)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(
        (response) => {
          this.loading = false;
          const mensajes = response?.Mensajes as any[] | undefined;
          if (mensajes && mensajes.length > 0) {
            const msg = mensajes[0];
            this.snackvar_service.openSnackBar(
              msg?.DescripcionMensaje ?? "Error",
              "",
              "red-snackbar",
              msg?.CodigoMensaje
            );
          } else {
            this.snackvar_service.openSnackBar(
              this.translation?.MensajeSuccessSnackBar ?? "Guardado correctamente",
              "",
              "green-snackbar"
            );
            // Notifica a tabela para recarregar e fecha o painel,
            // seguindo o padrão das outras janelas (save + destroy).
            this.save();
            this.destroy();
          }
          this.cdRef.detectChanges();
        },
        (error) => {
          this.loading = false;
          this.httpErrorService.identificarErrores(error);
          this.cdRef.detectChanges();
        }
      );
  }

  onNombreInput(event: Event): void {
    const input = event.target as HTMLInputElement | null;
    if (!input) return;
    const valor = input.value ?? "";
    const sanitizado = EditComplementoV11Component.sanitizarTexto(valor);
    if (sanitizado !== valor) {
      const ctrl = this.complementoForm.get("Nombre");
      if (ctrl) {
        ctrl.setValue(sanitizado, { emitEvent: true });
      }
      input.value = sanitizado;
    }
  }

  onDescripcionInput(event: Event): void {
    const input = event.target as HTMLTextAreaElement | null;
    if (!input) return;
    const valor = input.value ?? "";
    const sanitizado = EditComplementoV11Component.sanitizarTexto(valor);
    if (sanitizado !== valor) {
      const ctrl = this.complementoForm.get("Descripcion");
      if (ctrl) {
        ctrl.setValue(sanitizado, { emitEvent: true });
      }
      input.value = sanitizado;
    }
  }

  /**
   * Validação de caracteres não permitidos pelo backend:
   * - Não permite o caractere ';'
   * - Não permite a combinação '@@'
   */
  forbiddenCharsValidator(control: AbstractControl) {
    const value = (control.value ?? "") as string;
    if (!value) return null;
    const encontrado = EditComplementoV11Component.TEXTO_PROIBIDO.find((seq) =>
      value.includes(seq)
    );
    return encontrado ? { forbiddenChars: true } : null;
  }

  getErrorMessage(field: string): string {
    const fc = this.complementoForm.get(field);
    if (fc?.errors) {
      if (fc.errors["required"]) return this.translation?.CampoRequerido ?? "Campo obligatorio";
      if (fc.errors["forbiddenChars"]) {
        return (
          this.translation?.CaracteresNoPermitidos ??
          "No se permite el carácter ';' ni la combinación '@@'."
        );
      }
      if (fc.errors["maxlength"])
        return (this.translation?.LongitudMaxima ?? "Longitud máxima") + " " + fc.errors["maxlength"].requiredLength;
    }
    return "";
  }

  validateFields(field: string): boolean {
    const fc = this.complementoForm.get(field);
    return fc != null && fc.invalid && (fc.dirty || fc.touched);
  }
}
